import { Tooltip } from '@snack-uikit/tooltip';
import { Typography, TypographyProps } from '@snack-uikit/typography';
import cn from 'classnames';

import { HighLightCode } from '@/components/static';
import { DYNAMIC_CONTENT_CLASS_NAMES } from '@/constant/dynamic';
import { DATA_TEST_IDS_INJECTORS } from '@/constant/tests';
import { either, equals } from '@/utils/common';
import { removeEmptyStrings } from '@/utils/strings';

import { collectTextContent } from '../utils';
import s from './styles.module.scss';
import { getNodeClass, makeInjector, nodeClassIs, nodeNameIs } from './utils';

const getHTagWrapper = (nodeName: string) => {
  const nodeNameIs = equals(nodeName);
  if (nodeNameIs('h1')) return Typography.SansHeadlineM;
  if (nodeNameIs('h2')) return Typography.SansHeadlineS;
  if (nodeNameIs('h3')) return Typography.SansTitleL;
  return Typography.SansHeadlineM;
};

type Tag = Required<TypographyProps>['tag'];

export const hToSnackTypographyInjector = makeInjector((node, children) => {
  const hTags = ['h1', 'h2', 'h3'];
  const nodeName = node.name;
  if (!hTags.includes(nodeName)) return null;

  const Wrapper = getHTagWrapper(nodeName);
  return (
    <Wrapper
      data-test-id={DATA_TEST_IDS_INJECTORS[nodeName as keyof typeof DATA_TEST_IDS_INJECTORS]}
      className={s.article_h_wrapper}
      tag={nodeName as Tag}
    >
      {collectTextContent(children)}
    </Wrapper>
  );
});

export const pToSnackTypographyInjector = makeInjector((node, children) => {
  const isPElementNode = equals(getNodeClass(node), DYNAMIC_CONTENT_CLASS_NAMES.boldP);
  const TypographyComponent = isPElementNode ? Typography.SansTitleM : Typography.SansBodyL;

  return (
    <span id={node.attribs.id}>
      <TypographyComponent data-test-id={DATA_TEST_IDS_INJECTORS.p} className={s.snack_p_wrapper} tag='p'>
        {children}
      </TypographyComponent>
    </span>
  );
}, nodeNameIs('p'));

export const guidLabelToSnackTypographyInjector = makeInjector(
  (node, children) => (
    <Typography.SansTitleM data-test-id={DATA_TEST_IDS_INJECTORS.guidLabel} tag={node.name as Tag}>
      {children}
    </Typography.SansTitleM>
  ),
  either(nodeClassIs(DYNAMIC_CONTENT_CLASS_NAMES.guilabel), nodeClassIs(DYNAMIC_CONTENT_CLASS_NAMES.menuselection)),
);

export const dtToSnackTypographyInjector = makeInjector(
  (node, children) => (
    <dt id={node.attribs.id} data-test-id={DATA_TEST_IDS_INJECTORS.dt} className={s.dt_wrapper}>
      <Typography.SansTitleM>{collectTextContent(children)}</Typography.SansTitleM>
    </dt>
  ),
  nodeNameIs('dt'),
);

export const tagCodeInjector = makeInjector(
  (_, children) => (
    <Typography.SansBodyM data-test-id={DATA_TEST_IDS_INJECTORS.code} tag='span' className={s.code_block_wrapper}>
      {collectTextContent(children)}
    </Typography.SansBodyM>
  ),
  nodeNameIs('code'),
);

export const kbdToSnackTypographyInjector = makeInjector((node, children) => {
  const stringContent = collectTextContent(children);
  const parent = node.parent;
  if (parent && 'name' in parent && equals(parent.name, 'dt')) {
    return (
      <Typography.MonoBodyM data-test-id={DATA_TEST_IDS_INJECTORS.kbd} tag='span' className={s.dl_dt_kbt_wrapper}>
        {stringContent}
      </Typography.MonoBodyM>
    );
  }

  return (
    <Typography.MonoBodyL data-test-id={DATA_TEST_IDS_INJECTORS.kbd} tag='span' className={s.code_block_wrapper}>
      {removeEmptyStrings(stringContent)}
    </Typography.MonoBodyL>
  );
}, nodeNameIs('kbd'));

export const highlightInjector = makeInjector((_, children) => {
  return <HighLightCode code={collectTextContent(children).join('')} lang={'bash'} />;
}, nodeClassIs(DYNAMIC_CONTENT_CLASS_NAMES.highlight));

export const abbrInjector = makeInjector(
  (node, children) => (
    <Tooltip triggerClassName={s.abbr_dash} tip={node.attribs.title}>
      {collectTextContent(children).join(' ')}
    </Tooltip>
  ),
  nodeNameIs('abbr'),
);

export const commandInjector = makeInjector(
  (node, children) => {
    const isCommandCN = equals(node.attribs.class, DYNAMIC_CONTENT_CLASS_NAMES.command);
    const TextWrapper = isCommandCN ? Typography.SansBodyM : Typography.SansTitleM;
    return (
      <TextWrapper
        data-test-id={DATA_TEST_IDS_INJECTORS.command}
        tag='span'
        className={isCommandCN ? cn(s.code_block_wrapper, s.red_text) : undefined}
      >
        {collectTextContent(children)}
      </TextWrapper>
    );
  },
  either(nodeNameIs('strong'), nodeClassIs(DYNAMIC_CONTENT_CLASS_NAMES.command)),
);
